import {
  BEGIN_LOGGING,
  CallbackAction,
  DD_LOG_EVENT,
  GEOLOCATION_INVALIDATED,
  GEOLOCATION_PASSED,
  GEOLOCATION_REJECTED,
  GeolocationCallback,
  LOCATION_FAILURE,
  LOCATION_IN_PROGRESS,
  LOG_EVENT,
} from '@fanduel/geolocation-sdk';
import { useCallback } from 'react';

import { WRAPPER_TO_GAME_CLIENT_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/game-client-events/types';
import { validateAndNormalizePayload } from '@/features/geolocation/geolocation-payload-helpers';
import { useGeolocationHandlers } from '@/features/geolocation/use-geolocation-handlers';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';

export const useGeolocationCallback = () => {
  const {
    handleGeolocationPassed,
    handleGeolocationRejected,
    handleLocationFailure,
    handleLocationInProgress,
    handleAndroidGeolocationTokenUpdate,
    handleGeolocationTokenInvalidated,
    handleGeolocationLogEvent,
  } = useGeolocationHandlers();

  const geolocationCallback: GeolocationCallback = useCallback(
    (action: CallbackAction) => {
      switch (action.type) {
        case LOG_EVENT:
          // Handle Log Event Action
          console.log('LOG_EVENT Geolocation', action);
          break;
        case GEOLOCATION_PASSED:
          // Handle Geolocation Passed Action
          handleGeolocationPassed(action);
          break;
        case GEOLOCATION_REJECTED:
          // Handle Geolocation Rejected Action
          handleGeolocationRejected(action);
          break;
        case LOCATION_FAILURE:
          // Handle Location Failure Action
          handleLocationFailure(action);
          break;
        case LOCATION_IN_PROGRESS:
          // Handle Location In Progress Action
          handleLocationInProgress();
          break;
        case GEOLOCATION_INVALIDATED:
          // Notifies the consuming app that a JWT token has become invalid, and the user no longer has verified their location. It passes the product, region and reason for invalidation.
          handleGeolocationTokenInvalidated(action);
          break;
        case BEGIN_LOGGING:
          // Notifies the consumer that they should begin logging geolocation data. It passes LoggerConfig and tags objects to configure the Datadog logger.
          break;
        case DD_LOG_EVENT:
          // This callback passes information on various events with logLevel, eventName and attributes which should be used to log data from the SDK.
          handleGeolocationLogEvent(action);
          break;
      }
    },
    [
      handleGeolocationPassed,
      handleGeolocationRejected,
      handleLocationFailure,
      handleLocationInProgress,
      handleGeolocationTokenInvalidated,
      handleGeolocationLogEvent,
    ],
  );

  const wrapperResponseHandler = useCallback(
    (event: MessageEvent) => {
      if (
        event.data.type ===
        WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GEOLOCATION_MESSAGE
      ) {
        const { payload } = event.data;
        const normalizedPayload = validateAndNormalizePayload(payload);

        if (normalizedPayload) {
          geolocationCallback(normalizedPayload);
        } else {
          // Invalid payload: log error and skip processing
          Logger.logRumError({
            name: 'Invalid geolocation payload structure',
            useCase: DdEventCategory.Geolocation,
            eventType: DdEventType.Error,
            service: 'geolocation-web',
            context: {
              payloadType: typeof payload,
              payloadValue: payload,
              eventDataType: event.data?.type,
            },
            description:
              'Received geolocation message with invalid payload structure. Expected CallbackAction with type property.',
          });
        }
      }
      if (
        event.data.type ===
        WRAPPER_TO_GAME_CLIENT_EVENT_TYPES.GEOLOCATION_UPDATE
      ) {
        handleAndroidGeolocationTokenUpdate(event.data.payload);
      }
    },
    [geolocationCallback, handleAndroidGeolocationTokenUpdate],
  );

  return { geolocationCallback, wrapperResponseHandler };
};
